/**
 * Canonical insurance-name handling — the single source of truth shared by
 * insuranceStore (the editor that creates/lists insurances) and providerStore
 * (the resolver that maps selected names back to relation documentIds).
 *
 * MWBI-844 — these two stores previously normalized names differently, so a
 * typed name like "Blue  Cross" was stored canonically as "Blue Cross" but kept
 * un-collapsed in the provider edit form. The resolver's lookup then missed and
 * the just-added insurance was silently dropped from the relation. Reusing one
 * normalizer here keeps both sides reading the same canonical names.
 */

/** Trim and collapse internal runs of whitespace to a single space. */
export const normalizeInsuranceName = (name: string): string => name.trim().replace(/\s+/g, ' ');

/** Case-insensitive dedupe/lookup key derived from the normalized name. */
export const getInsuranceKey = (name: string): string => normalizeInsuranceName(name).toLowerCase();

/**
 * Drop entries whose normalized name is empty or a case-insensitive duplicate
 * of an earlier entry, preserving first-seen order.
 */
export const dedupeByInsuranceName = <T extends { name: string }>(items: T[]): T[] => {
  const seen = new Set<string>();

  return items.filter((item) => {
    const key = getInsuranceKey(item.name);
    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};
